// src/pages/ContactPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue, get } from "firebase/database";
import { db } from "../lib/firebase";
import DynamicForm from "../components/DynamicForm";
import ContactForm from "../components/ContactForm";
import PdfDocumentSection from "../components/PdfDocumentSection";
import usePdfConfig from "../hooks/usePdfConfig";
import { getPdfDocumentsForSection } from "../utils/pdfDocuments";
import "./ContactPage.css";

import { CONFIG } from "../config";
const IMAGES_BASE = CONFIG.IMAGES_BASE;
const DEFAULT_WEB3FORMS_ACCESS_KEY = String(CONFIG.WEB3FORMS_ACCESS_KEY || "").trim();
const DEFAULT_FORM_NOTIFICATION_EMAIL = String(
  CONFIG.FORM_NOTIFICATION_EMAIL || ""
).trim();

/** Helpers */
const urlFromBackend = (name) =>
  name ? `${IMAGES_BASE}/${encodeURIComponent(name)}` : "";

const normalizeContact = (v) => {
  const normalizePhoneEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === "string") {
      return { number: entry, showOnFooter: true };
    }
    if (typeof entry === "object") {
      return { showOnFooter: true, ...entry };
    }
    return null;
  };
  const normalizeEmailEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === "string") {
      return { value: entry, showOnFooter: true };
    }
    if (typeof entry === "object") {
      return { showOnFooter: true, ...entry };
    }
    return null;
  };

  const phones = (
    Array.isArray(v?.phones) && v.phones.length
      ? v.phones
      : v?.phone
      ? [v.phone]
      : []
  )
    .map(normalizePhoneEntry)
    .filter(Boolean);
  const emails = (
    Array.isArray(v?.emails) && v.emails.length
      ? v.emails
      : v?.email
      ? [v.email]
      : []
  )
    .map(normalizeEmailEntry)
    .filter(Boolean);
  const social = Array.isArray(v?.socialmedia) ? v.socialmedia : [];

  return {
    bannerDesktop: v?.bannerDesktop || "",
    bannerMobile: v?.bannerMobile || "",
    phones,
    emails,
    social,
    address: v?.address || "",
    mapEmbedUrl: v?.mapEmbedUrl || "",
    formConfigId: v?.formConfigId || "",
    contactFormAccessKey:
      v?.contactFormAccessKey || DEFAULT_WEB3FORMS_ACCESS_KEY,
    submissionEmail: v?.submissionEmail || DEFAULT_FORM_NOTIFICATION_EMAIL,
  };
};

export default function ContactPage() {
  const { pdfConfig } = usePdfConfig();
  const contactPdfDocuments = useMemo(
    () => getPdfDocumentsForSection(pdfConfig?.documents, "contact"),
    [pdfConfig?.documents]
  );
  const [raw, setRaw] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formConfig, setFormConfig] = useState(null);
  const [formLoading, setFormLoading] = useState(true);

  const mounted = useRef(true);

  // Load contactconfig
  useEffect(() => {
    mounted.current = true;
    const off = onValue(
      ref(db, "contactconfig"),
      (snap) => {
        if (!mounted.current) return;
        const v = snap.val() || {};
        setRaw(v);
        setData(normalizeContact(v));
        setLoading(false);
      },
      (err) => {
        console.error("contactconfig load error:", err);
        setLoading(false);
      }
    );
    return () => {
      mounted.current = false;
      if (typeof off === "function") off();
    };
  }, []);

  // Load selected dynamic form if any
  useEffect(() => {
    const formId = raw?.formConfigId;
    if (!formId) {
      setFormConfig(null);
      setFormLoading(false);
      return;
    }

    let cancelled = false;
    setFormLoading(true);

    (async () => {
      try {
        const snap = await get(ref(db, `formConfigs/${formId}`));
        if (!cancelled) {
          if (snap.exists()) {
            setFormConfig({ id: formId, ...snap.val() });
          } else {
            setFormConfig(null);
          }
        }
      } catch (e) {
        console.error("formConfig load error:", e);
        if (!cancelled) setFormConfig(null);
      } finally {
        if (!cancelled) setFormLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [raw?.formConfigId]);

  // Contact details + socials (only those marked showOnFooter)
  const contactPhones = useMemo(
    () => data?.phones?.filter((p) => p.showOnFooter && p.number) || [],
    [data]
  );
  const contactEmails = useMemo(
    () => data?.emails?.filter((e) => e.showOnFooter && e.value) || [],
    [data]
  );
  const primaryEmail = useMemo(() => {
    const allEmails = Array.isArray(data?.emails) ? data.emails : [];
    const firstEmail = allEmails.find((entry) => entry?.value);
    return String(firstEmail?.value || "").trim();
  }, [data?.emails]);
  const shouldShowContactPdf = contactPdfDocuments.length > 0;
  const hasDynamicForm = !!raw?.formConfigId && !!formConfig;
  const pageLoading = loading || (!!raw?.formConfigId && formLoading);

  if (pageLoading) {
    return <div className="cp-loading">Loading contact details…</div>;
  }

  if (!data) {
    return <div className="cp-loading">No contact configuration found.</div>;
  }

  const hasHeroDesktop = !!data.bannerDesktop;
  const hasHeroMobile = !!data.bannerMobile;

  const locationLabel = (() => {
    const addr = data.address || "";
    if (!addr) return "";
    if (/bengaluru|bangalore/i.test(addr)) return "Bangalore";
    const first = addr.split(",")[0]?.trim();
    return first || "";
  })();

  const PhoneIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 4h3l2 5-2 1c1.5 3 4 5.5 7 7l1-2 5 2v3c0 1-1 2-2 2-8.3 0-15-6.7-15-15 0-1 1-2 2-2z"
        fill="currentColor"
      />
    </svg>
  );

  const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zm0 2v.5l8 5 8-5V7H4zm16 10V10l-8 5-8-5v7h16z"
        fill="currentColor"
      />
    </svg>
  );

  const PinIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2a7 7 0 0 1 7 7c0 4.5-7 13-7 13S5 13.5 5 9a7 7 0 0 1 7-7zm0 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <div className="cp-page">
      {/* ===== HERO SECTION (Banner + Overlay text) ===== */}
      <section className="cp-hero">
        {(hasHeroDesktop || hasHeroMobile) && (
          <picture>
            {hasHeroMobile && (
              <source
                media="(max-width: 768px)"
                srcSet={urlFromBackend(data.bannerMobile)}
              />
            )}
            {hasHeroDesktop && (
              <img
                src={urlFromBackend(
                  data.bannerDesktop || data.bannerMobile
                )}
                alt="Contact banner"
                className="cp-hero-img"
              />
            )}
          </picture>
        )}

        <div className="cp-hero-overlay" />
        <div className="cp-hero-content">
          <h1 className="cp-hero-title">CONTACT US</h1>
          <div className="cp-hero-line" />
        </div>
      </section>

      {/* ===== MAIN GRID: LEFT (details) + RIGHT (form) ===== */}
      <section className="cp-main">
        <div className="cp-grid">
          {/* LEFT COLUMN – CONTACT CARDS */}
          <div className="cp-left">
            <div className="cp-info-block">
              <h2 className="cp-info-title">GET IN TOUCH</h2>
              <p className="cp-info-sub">
                We’d love to hear from you! Whether you have questions, feedback,
                or inquiries about our services, feel free to reach out. You can
                contact us via the form below, or use the details provided to
                connect with us directly.
              </p>

              <div className="cp-location">
                {locationLabel && (
                  <h3 className="cp-location-title">{locationLabel}</h3>
                )}
                <ul className="cp-contact-list">
                  {contactPhones.map((p, idx) => (
                    <li key={`phone-${idx}`} className="cp-contact-item">
                      <span className="cp-contact-icon">
                        <PhoneIcon />
                      </span>
                      <a
                        href={`tel:${p.number.replace(/[^\d+]/g, "")}`}
                        className="cp-contact-text"
                      >
                        {p.number}
                      </a>
                    </li>
                  ))}
                  {contactEmails.map((e, idx) => (
                    <li key={`email-${idx}`} className="cp-contact-item">
                      <span className="cp-contact-icon">
                        <MailIcon />
                      </span>
                      <a href={`mailto:${e.value}`} className="cp-contact-text">
                        {e.value}
                      </a>
                    </li>
                  ))}
                  {data.address && (
                    <li className="cp-contact-item">
                      <span className="cp-contact-icon">
                        <PinIcon />
                      </span>
                      <span className="cp-contact-text">{data.address}</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN – FORM (centered vertically & horizontally) */}
          <div className="cp-right">
            <div className="cp-form-shell">
              <div className="cp-form-card">
                {formLoading && raw?.formConfigId ? (
                  <div className="cp-form-loading">Loading form…</div>
                ) : hasDynamicForm ? (
                  <DynamicForm
                    form={formConfig}
                    notificationEmail={
                      data?.submissionEmail || formConfig?.notificationEmail || primaryEmail
                    }
                  />
                ) : (
                  <ContactForm
                    config={{
                      emails: contactEmails.map((e) => e.value),
                      phones: contactPhones.map((p) => p.number),
                      accessKey: data?.contactFormAccessKey || "",
                      notificationEmail: data?.submissionEmail || primaryEmail,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MAP SECTION ===== */}
      {data.mapEmbedUrl && (
        <section className="cp-map-section">
          <div className="cp-map-frame">
            <iframe
              title="Location map"
              src={data.mapEmbedUrl}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>
      )}

      {shouldShowContactPdf && (
        <section className="cp-documents">
          <PdfDocumentSection
            documents={contactPdfDocuments}
            title="Documents"
            subtitle="Tap a card to preview the full PDF."
            mode="section"
            styleConfig={pdfConfig?.styleConfig}
          />
        </section>
      )}
    </div>
  );
}
